import { z } from 'zod';
import type { NativeGitGenerationContext } from './git';

/** Maximum number of diff characters sent to the generation agent. */
const GENERATION_DIFF_LIMIT = 60_000;

/** Kinds of Git text the environment menu can generate. */
export type GenerationKind = 'commit' | 'pr';

export const commitMetadataSchema = z.object({ message: z.string().trim().min(1) });
export const pullRequestMetadataSchema = z.object({
  title: z.string().trim().min(1),
  body: z.string().trim().min(1),
});

export type CommitMetadata = z.infer<typeof commitMetadataSchema>;
export type PullRequestMetadata = z.infer<typeof pullRequestMetadataSchema>;

export const COMMIT_METADATA_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['message'],
  properties: { message: { type: 'string', minLength: 1 } },
} as const;

export const PULL_REQUEST_METADATA_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['title', 'body'],
  properties: {
    title: { type: 'string', minLength: 1 },
    body: { type: 'string', minLength: 1 },
  },
} as const;

/**
 * Builds a stable scope key identifying a workspace/branch pair, used to detect
 * when generated Git text should be reset.
 */
export function generationScopeKey(workspace: string, branch: string | null): string {
  return `${workspace}\0${branch ?? ''}`;
}

/** Builds the prompt asking the agent for a commit message. */
export function buildCommitPrompt(
  context: Pick<NativeGitGenerationContext, 'recentCommits' | 'commitDiff'>,
  limit: number = GENERATION_DIFF_LIMIT,
): string {
  return [
    'Generate a Git commit message for the supplied changes.',
    'Return exactly JSON matching {"message":"..."}.',
    'Use the repository recent commit style. Keep the subject concise and imperative; add a body only when it materially helps.',
    '',
    `Recent commits:\n${context.recentCommits || '-'}`,
    '',
    `Current diff:\n${context.commitDiff.slice(0, limit) || '-'}`,
  ].join('\n');
}

/** Builds the prompt asking the agent for a pull request title and body. */
export function buildPullRequestPrompt(
  params: {
    branch: string | null;
    commitMessage: string;
    context: Pick<NativeGitGenerationContext, 'recentCommits' | 'pullRequestDiff' | 'baseBranch'>;
  },
  limit: number = GENERATION_DIFF_LIMIT,
): string {
  const { branch, commitMessage, context } = params;
  return [
    'Generate a pull request title and Markdown body for the supplied branch changes.',
    'Return exactly JSON matching {"title":"...","body":"..."}.',
    'The title must be concise. The body must explain what changed and why, then include a Testing section based only on evidence in the diff; do not invent tests.',
    '',
    `Head branch: ${branch ?? '-'}`,
    `Base branch: ${context.baseBranch ?? '-'}`,
    `Existing commit message context: ${commitMessage || '-'}`,
    '',
    `Recent commits:\n${context.recentCommits || '-'}`,
    '',
    `Branch diff:\n${context.pullRequestDiff.slice(0, limit) || '-'}`,
  ].join('\n');
}
